/**
 * Central HTTP client for SnapHub PWA.
 *
 * Provides `get`, `post`, `put`, `delete` methods with:
 * - Automatic `Authorization: Bearer <token>` header
 * - CSRF token from cookie `XSRF-TOKEN` as `X-CSRF-Token` header for POST/PUT/DELETE
 * - Automatic token refresh on 401 (retry once)
 * - Retry on 429 with `Retry-After` header
 * - Redirect to `/login` on refresh failure
 * - User-friendly German error messages for 400, 403, 404, 429, 500
 * - Network error handling
 *
 * Validates: Requirements 3.1–3.3, 5.1, 5.2, 43.1–43.4, 46.1
 */

import { authService } from '../../features/auth/auth.service';
import type { navigate } from '../../infrastructure/router';

/**
 * Lazily navigate via the router.
 *
 * The router is imported dynamically (not at module top-level) to avoid an
 * import cycle: router.ts → customer-required-overlay → customer.service →
 * `new ApiClient()`. A static `import { navigate }` here would leave ApiClient
 * undefined while that cycle resolves, causing "ApiClient is not a constructor".
 */
function lazyNavigate(path: string): void {
  void import('../../infrastructure/router').then(({ navigate }) => navigate(path));
}

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

function getApiBaseUrl(): string {
  try {
    return (import.meta.env?.VITE_BASE_API_BASE_URL as string | undefined) ?? '';
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RequestOptions {
  timeout?: number;
  skipAuth?: boolean;
  skipCsrf?: boolean;
}

// ---------------------------------------------------------------------------
// ApiError
// ---------------------------------------------------------------------------

/**
 * Custom error class thrown by the ApiClient.
 * Contains the HTTP status, a user-friendly message, and the original Response.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly response?: Response,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// ---------------------------------------------------------------------------
// Error message mapping
// ---------------------------------------------------------------------------

const ERROR_MESSAGES: Record<number, string> = {
  400: 'Ungültige Anfrage. Bitte überprüfen Sie Ihre Eingaben.',
  403: 'Keine Berechtigung für diese Aktion.',
  404: 'Die angeforderte Ressource wurde nicht gefunden.',
  429: 'Zu viele Anfragen. Bitte warten Sie einen Moment.',
  500: 'Serverfehler. Bitte versuchen Sie es später erneut.',
};

const NETWORK_ERROR_MESSAGE =
  'Keine Internetverbindung. Bitte prüfen Sie Ihre Verbindung.';

/**
 * Returns a user-friendly German error message for the given HTTP status code.
 * Falls back to a generic message for unknown codes.
 */
export function getErrorMessage(status: number): string {
  return ERROR_MESSAGES[status] ?? `Unbekannter Fehler (${status}).`;
}

// ---------------------------------------------------------------------------
// CSRF helper
// ---------------------------------------------------------------------------

/**
 * Reads the CSRF token from the `XSRF-TOKEN` cookie.
 * Returns `undefined` when the cookie is not present.
 */
export function getCsrfToken(): string | undefined {
  try {
    return document.cookie
      .split('; ')
      .find((row) => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];
  } catch {
    return undefined;
  }
}

// ---------------------------------------------------------------------------
// ApiClient class
// ---------------------------------------------------------------------------

export class ApiClient {
  private baseUrl: string;
  private authServiceRef: typeof authService;
  private navigateFn: typeof navigate;

  constructor(
    baseUrl?: string,
    authSvc?: typeof authService,
    nav?: typeof navigate,
  ) {
    this.baseUrl = baseUrl ?? getApiBaseUrl();
    this.authServiceRef = authSvc ?? authService;
    this.navigateFn = nav ?? lazyNavigate;
  }

  // -- Public HTTP methods --------------------------------------------------

  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('GET', url, undefined, options);
  }

  async post<T>(url: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('POST', url, body, options);
  }

  async put<T>(url: string, body: unknown, options?: RequestOptions): Promise<T> {
    return this.request<T>('PUT', url, body, options);
  }

  async delete<T>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>('DELETE', url, undefined, options);
  }

  // -- Core request logic ---------------------------------------------------

  private async request<T>(
    method: string,
    url: string,
    body?: unknown,
    options?: RequestOptions,
    isRetry = false,
  ): Promise<T> {
    const headers: Record<string, string> = {};

    // Content-Type only for requests with a body
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    // Authorization header
    if (!options?.skipAuth) {
      const token = this.authServiceRef.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    // CSRF header for state-changing methods
    const isStateChanging = method === 'POST' || method === 'PUT' || method === 'DELETE';
    if (isStateChanging && !options?.skipCsrf) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    const fullUrl = `${this.baseUrl}${url}`;

    const fetchOptions: RequestInit = {
      method,
      headers,
    };

    if (body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    // Timeout support
    let controller: AbortController | undefined;
    if (options?.timeout) {
      controller = new AbortController();
      fetchOptions.signal = controller.signal;
      setTimeout(() => controller!.abort(), options.timeout);
    }

    let response: Response;
    try {
      response = await fetch(fullUrl, fetchOptions);
    } catch {
      throw new ApiError(0, NETWORK_ERROR_MESSAGE);
    }

    // -- Handle 401: token refresh + retry once --
    if (response.status === 401 && !isRetry && !options?.skipAuth) {
      const refreshed = await this.authServiceRef.refreshTokenAsync();
      if (refreshed) {
        return this.request<T>(method, url, body, options, true);
      }
      // Refresh failed → redirect to login
      this.navigateFn('/login');
      throw new ApiError(401, getErrorMessage(401), response);
    }

    // -- Handle 429: respect Retry-After and retry --
    if (response.status === 429 && !isRetry) {
      const retryAfter = response.headers.get('Retry-After');
      const waitMs = retryAfter ? parseFloat(retryAfter) * 1000 : 1000;
      await this.delay(waitMs);
      return this.request<T>(method, url, body, options, true);
    }

    // -- Handle other error statuses --
    if (!response.ok) {
      const message = getErrorMessage(response.status);
      throw new ApiError(response.status, message, response);
    }

    // -- Parse successful response --
    const text = await response.text();
    if (!text) {
      return undefined as T;
    }
    return JSON.parse(text) as T;
  }

  // -- Helpers --------------------------------------------------------------

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------

/** Global ApiClient shared across the entire application. */
export const apiClient = new ApiClient();
